<template>
  <div v-if="draft" class="pp-style" :style="{ '--pp-color': companyColor }">
    <p class="pp-style__label">Cor principal</p>
    <div class="pp-style__colors">
      <button
        v-for="color in colors"
        :key="color"
        type="button"
        class="pp-style__color"
        :class="{ 'pp-style__color--active': isAccent(color) }"
        :style="{ background: color }"
        :aria-label="`Cor ${color}`"
        :aria-pressed="isAccent(color) ? 'true' : 'false'"
        @click="setTheme({ accent: color })"
      />
    </div>

    <p class="pp-style__label">Tipografia</p>
    <div class="pp-style__grid pp-style__grid--3">
      <button
        v-for="font in fonts"
        :key="font.id"
        type="button"
        class="pp-style__option pp-style__option--font"
        :class="{ 'pp-style__option--active': theme.font === font.id }"
        @click="setTheme({ font: font.id })"
      >
        <span class="pp-style__aa" :style="{ fontFamily: font.family }">Aa</span>
        <small :style="{ fontFamily: font.family }">{{ font.label }}</small>
      </button>
    </div>

    <p class="pp-style__label">Foto</p>
    <div class="pp-style__grid pp-style__grid--3">
      <button
        v-for="shape in photoShapes"
        :key="shape.id"
        type="button"
        class="pp-style__option"
        :class="{ 'pp-style__option--active': theme.photoShape === shape.id }"
        @click="setTheme({ photoShape: shape.id })"
      >
        {{ shape.label }}
      </button>
    </div>

    <p class="pp-style__label">Botões</p>
    <div class="pp-style__grid pp-style__grid--3">
      <button
        v-for="shape in buttonShapes"
        :key="shape.id"
        type="button"
        class="pp-style__option"
        :class="{ 'pp-style__option--active': theme.buttonShape === shape.id }"
        @click="setTheme({ buttonShape: shape.id })"
      >
        <span
          class="pp-style__button-sample"
          :style="{ borderRadius: shape.radius, background: theme.accent || colors[0] }"
        />
        {{ shape.label }}
      </button>
    </div>

    <template v-if="draft.template === 'platformer'">
      <p class="pp-style__label">Cenário</p>
      <div class="pp-style__scenes">
        <button
          v-for="scene in scenes"
          :key="scene.id"
          type="button"
          class="pp-style__scene"
          :class="{ 'pp-style__scene--active': (draft.platformer_scene || 'fantasy') === scene.id }"
          @click="edit({ platformer_scene: scene.id })"
        >
          <span class="pp-style__scene-art" :style="{ background: scene.sky }">
            <img :src="sceneThumb(scene)" alt="" />
          </span>
          <small>{{ scene.label }}</small>
        </button>
      </div>

      <p class="pp-style__label">Personagem</p>
      <p class="pp-style__hint">Quem anda pela fase do seu perfil.</p>
      <div class="pp-style__characters">
        <button
          v-for="character in characters"
          :key="character.id"
          type="button"
          class="pp-style__character"
          :class="{ 'pp-style__character--active': (draft.character || 'w1') === character.id }"
          :title="character.label"
          :aria-label="character.label"
          :aria-pressed="(draft.character || 'w1') === character.id ? 'true' : 'false'"
          @click="edit({ character: character.id })"
        >
          <img :src="characterThumb(character.id)" alt="" />
        </button>
      </div>
      <div class="pp-style__branding pp-style__game-only">
        <div>
          <strong>Mostrar o perfil organizado embaixo do jogo</strong>
          <small>Desligado, a página mostra só o jogo.</small>
        </div>
        <vs-switch
          :color="companyColor"
          :value="!draft.game_only"
          @input="edit({ game_only: !$event })"
        />
      </div>
    </template>

    <template v-if="draft.template === 'animated'">
      <p class="pp-style__label">Fundo da apresentação</p>
      <div class="pp-style__backdrops">
        <button
          v-for="option in backdrops"
          :key="option.id"
          type="button"
          class="pp-style__backdrop"
          :class="{ 'pp-style__backdrop--active': (draft.presentation_backdrop || 'night') === option.id }"
          @click="edit({ presentation_backdrop: option.id })"
        >
          <span class="pp-style__swatch" :style="{ background: option.swatch }" />
          <small>{{ option.label }}</small>
        </button>
      </div>

      <p class="pp-style__label">Ritmo</p>
      <p class="pp-style__hint">Calmo dá mais tempo para ler cada cena.</p>
      <div class="pp-style__grid pp-style__grid--3">
        <button
          v-for="option in paces"
          :key="option.id"
          type="button"
          class="pp-style__option"
          :class="{ 'pp-style__option--active': (draft.presentation_pace || 'normal') === option.id }"
          @click="edit({ presentation_pace: option.id })"
        >
          {{ option.label }}
        </button>
      </div>
    </template>

    <p class="pp-style__label">Botões e rodapé</p>
    <p class="pp-style__hint">No PRO, você escolhe o que aparece.</p>
    <div v-for="option in proOptions" :key="option.flag" class="pp-style__branding">
      <div>
        <strong>{{ option.title }}</strong>
        <small>{{ option.text }}</small>
      </div>
      <div class="pp-style__branding-switch">
        <pro-badge v-if="!isPro" />
        <vs-switch
          :color="companyColor"
          :value="!draft[option.flag]"
          @input="setShown(option.flag, $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import ProBadge from "./ProBadge.vue";
import mixin from "./mixin";
import {
  ACCENT_COLORS,
  BUTTON_SHAPES,
  FONTS,
  PHOTO_SHAPES,
  PLATFORMER_CHARACTERS,
  PRESENTATION_BACKDROPS,
  PRESENTATION_PACES,
} from "./constants";
import { PROFILE_URL } from "@/services/publicProfile";

const FONT_LINK_ID = "pp-style-fonts";

export default {
  name: "StylePanel",
  components: { ProBadge },
  mixins: [mixin],
  computed: {
    ...mapState("publicProfile", ["draft"]),
    ...mapGetters("publicProfile", ["isPro"]),
    theme() {
      return this.draft.theme || {};
    },
    // Tirar qualquer um destes é PRO: no grátis eles sempre aparecem.
    proOptions: () => [
      {
        flag: "hide_resume_download",
        title: 'Botão "Baixar currículo"',
        text: "Quem visita baixa seu currículo em PDF.",
      },
      {
        flag: "hide_share",
        title: 'Botão "Compartilhar"',
        text: "Quem visita manda seu perfil para outras pessoas.",
      },
      {
        flag: "hide_branding",
        title: 'Selo "Perfil criado na Recrutei"',
        text: "Aparece no fim do perfil.",
      },
    ],
    characters: () => PLATFORMER_CHARACTERS,
    // Cenários do Plataforma; a miniatura é o "morro" de cada um sobre o céu dele.
    scenes: () => [
      { id: "fantasy", label: "Fantasia", path: "platformer", sky: "linear-gradient(180deg, #4169e1, #c8e0ff)" },
      { id: "city", label: "Cidade à noite", path: "platformer/city", sky: "linear-gradient(180deg, #06152b, #6554c0)" },
    ],
    backdrops: () => PRESENTATION_BACKDROPS,
    paces: () => PRESENTATION_PACES,
    colors: () => ACCENT_COLORS,
    fonts: () => FONTS,
    photoShapes: () => PHOTO_SHAPES,
    buttonShapes: () => BUTTON_SHAPES,
  },
  mounted() {
    // Roboto Serif e Roboto Mono so servem para a amostra "Aa": carrega sob demanda.
    if (!document.getElementById(FONT_LINK_ID)) {
      const link = document.createElement("link");
      link.id = FONT_LINK_ID;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400&family=Roboto+Serif:wght@400&display=swap";
      document.head.appendChild(link);
    }
  },
  methods: {
    isAccent(color) {
      return String(this.theme.accent || "").toLowerCase() === color.toLowerCase();
    },
    sceneThumb(scene) {
      return `${PROFILE_URL}/${scene.path}/hill.png`;
    },
    characterThumb(id) {
      return `${PROFILE_URL}/platformer/char-${id}-idle.png`;
    },
    edit(patch) {
      this.$store.dispatch("publicProfile/edit", { ...patch, _track: { origin: "style_panel" } });
    },
    setShown(flag, show) {
      // Esconder é PRO; quem é grátis vê o paywall e o botão continua.
      if (!show && !this.isPro) {
        this.ppTrack("public_profile_pro_cta_clicked", { cta: flag, option: flag });
        this.$emit("paywall", flag);
        return;
      }
      this.edit({ [flag]: !show });
    },
    setTheme(patch) {
      this.edit({ theme: { ...this.theme, ...patch } });
    },
  },
};
</script>

<style lang="scss">
.pp-style {
  font-family: Roboto, sans-serif;

  &__label {
    margin: 24px 0 10px;
    color: #333;
    font-size: 14px;
    font-weight: 500;

    &:first-child {
      margin-top: 0;
    }
  }

  &__scenes {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  &__scene {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 6px;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    background: #fff;
    color: #4f4f4f;
    font-family: Roboto, sans-serif;
    cursor: pointer;

    small {
      font-size: 12px;
    }

    &--active {
      border-color: var(--pp-color);
      box-shadow: 0 0 0 2px var(--pp-color);
      color: #333;
    }
  }

  &__scene-art {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    height: 64px;
    overflow: hidden;
    border-radius: 6px;

    img {
      width: 90%;
      image-rendering: pixelated;
    }
  }

  &__characters {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
  }

  &__character {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    height: 72px;
    padding: 6px;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    background: linear-gradient(180deg, #c8e0ff, #e7edf0);
    cursor: pointer;

    img {
      max-height: 100%;
      image-rendering: pixelated;
    }

    &--active {
      border-color: var(--pp-color);
      box-shadow: 0 0 0 2px var(--pp-color);
    }
  }

  &__backdrops {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  &__backdrop {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 6px;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    background: #fff;
    color: #4f4f4f;
    font-family: Roboto, sans-serif;
    cursor: pointer;

    small {
      font-size: 12px;
    }

    &--active {
      border-color: var(--pp-color);
      box-shadow: 0 0 0 2px var(--pp-color);
      color: #333;
    }
  }

  &__swatch {
    display: block;
    height: 40px;
    border-radius: 6px;
  }

  &__game-only {
    margin-top: 14px;
  }

  &__hint {
    margin: -4px 0 12px;
    color: #696969;
    font-size: 13px;
  }

  &__branding {
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    strong {
      display: block;
      color: #333;
      font-size: 14px;
      font-weight: 400;
    }

    small {
      color: #696969;
      font-size: 13px;
    }
  }

  &__branding-switch {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 8px;
  }

  &__colors {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  &__color {
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 9999px;
    cursor: pointer;
    transition: box-shadow 0.2s ease;

    &--active {
      box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--pp-color);
    }
  }

  &__grid {
    display: grid;
    gap: 8px;

    &--3 {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  &__option {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 36px;
    padding: 6px 8px;
    border: 0.5px solid #a8a8a8;
    border-radius: 8px;
    background: #fff;
    color: #333;
    font-family: Roboto, sans-serif;
    font-size: 14px;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      border-color: #696969;
    }

    &--active,
    &--active:hover {
      border-color: var(--pp-color);
      box-shadow: 0 0 0 1px var(--pp-color);
    }

    &--font {
      flex-direction: column;
      gap: 2px;
      min-height: 76px;

      small {
        color: #696969;
        font-size: 12px;
      }
    }
  }

  &__aa {
    color: #333;
    font-size: 26px;
    line-height: 1.1;
  }

  &__button-sample {
    display: inline-block;
    flex-shrink: 0;
    width: 22px;
    height: 12px;
  }
}
</style>
